/**
 * Application utility functions
 */

export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatSidebarDate(day) {
  if (!day) return '';
  const parts = day.split('-');
  if (parts.length !== 3) return day;
  // Two inputs reach this: DD-MM-YYYY from the data backup sidebar and ISO
  // YYYY-MM-DD from upload automation, which reads shoot dates off the API
  // unchanged. A four digit first segment is the ISO one.
  const [dayPart, monthPart, yearPart] = parts[0].length === 4
    ? [parts[2], parts[1], parts[0]]
    : parts;
  const monthIndex = Number(monthPart) - 1;
  if (monthIndex < 0 || monthIndex > 11) return day;
  return `${Number(dayPart)} ${MONTH_LABELS[monthIndex]} ${yearPart}`;
}

/**
 * Check if user is admin
 * @returns {boolean} True if admin, false otherwise
 */
export function isAdmin() {
  var status = process.env.REACT_APP_IS_ADMIN === "true";
  return status;
}

/**
 * Format a byte size into a human-readable string.
 * @param {number} bytes
 * @returns {string} e.g. "1.5 MB", "0 B"
 */
export function formatFileSize(bytes) {
  if (!bytes || isNaN(bytes)) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

/**
 * Wait for AgentCommunicator (or AgentComms) constructor to be available on window.
 * Retries a few times with a delay.
 *
 * @param {number} maxRetries
 * @param {number} retryDelay
 * @returns {Promise<Function|null>}
 */
export async function waitForAgentCommunicator(maxRetries = 10, retryDelay = 500) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const AgentCommunicator = typeof window !== 'undefined'
      ? (window.AgentCommunicator || window.AgentComms)
      : null;

    if (AgentCommunicator && typeof AgentCommunicator === 'function') {
      return AgentCommunicator;
    }

    // Wait before next retry
    await new Promise((resolve) => setTimeout(resolve, retryDelay));
  }
  return null;
}

/**
 * Load the agent script once and wait for AgentCommunicator to become available.
 *
 * @param {string} url
 * @param {string} [scriptId='app-module-script']
 */
export async function loadAppScriptFromUrl(url, scriptId = 'app-module-script') {
  if (typeof document === 'undefined') return;

  const existingScript = document.getElementById(scriptId);
  if (existingScript) {
    const AgentCommunicator = typeof window !== 'undefined'
      ? (window.AgentCommunicator || window.AgentComms)
      : null;
    if (AgentCommunicator && typeof AgentCommunicator === 'function') {
      return;
    }
    // Script already in DOM (may still be loading). Wait for it; do NOT remove and re-add
    const AgentCommunicatorClass = await waitForAgentCommunicator();
    if (AgentCommunicatorClass) return;
    throw new Error('AgentCommunicator did not become available after script load');
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = url;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${url}`));
    document.head.appendChild(script);
  });
}

/**
 * Convert a string to title case (capitalize each word).
 *
 * @param {string} value
 * @returns {string}
 */
export function toTitleCase(value) {
  if (!value) return '';
  const str = String(value);
  return str
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get a color hex code based on a status-like key.
 *
 * @param {string} key
 * @returns {string}
 */
export function getColorForKey(key) {
  const colorMap = {
    approved: '#22c55e',
    rejected: '#ef4444',
    warnings: '#f59e0b',
    pending: '#6b7280',
    'under review': '#eab308',
  };
  return colorMap[key.toLowerCase()] || '#6b7280';
}

/**
 * Capitalize the first letter of a key for display labels.
 *
 * @param {string} key
 * @returns {string}
 */
export function getLabelFromKey(key) {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Copy text to the clipboard using the Clipboard API.
 * This mirrors the previous inline usage without adding new behavior.
 *
 * @param {string} text
 * @returns {Promise<void>}
 */
export function copyTextToClipboard(text) {
  return navigator.clipboard.writeText(text);
}

/**
 * Format a date value as a human-readable IST (Asia/Kolkata) string.
 *
 * @param {Date|string|number} date
 * @returns {string} e.g. "16 May, 02:45:30 pm"
 */
export function formatIST(date) {
  if (!date) return '';
  return new Date(date).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    day: '2-digit',
    month: 'short',
  });
}

export function getBookingPkFromRecord(record) {
  if (!record) return '';
  if (record.booking_pk != null && String(record.booking_pk).trim() !== '') {
    return String(record.booking_pk).trim();
  }
  if (record.booking_id != null && String(record.booking_id).trim() !== '') {
    return String(record.booking_id).trim();
  }
  return '';
}
